import React, { useState, useEffect, useMemo } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { Menu, Typography, useTheme } from "@mui/material";

import "../../../../css/documentManagement.css";

import { Filter } from "./Filter";
import { Sort } from "./Sort";

interface CustomGridFilterProps {
  columnName: string;
  sortable: boolean;
  filterable: boolean;
  data: string[];
  callbackApplyFilters: (_config: { sort: string; filters: string[] }) => void;
  // Initial state props
  initialSort?: string;
  initialFilters?: string[];
}

export const CustomGridFilter: React.FC<CustomGridFilterProps> = ({
  columnName,
  sortable,
  filterable,
  data,
  callbackApplyFilters,
  initialSort,
  initialFilters = [],
}) => {
  // Memoize initial values to prevent infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedInitialFilters = useMemo(() => initialFilters, [JSON.stringify(initialFilters)]);
  const memoizedInitialSort = useMemo(() => initialSort, [initialSort]);
  // Internal state for menu control
  const [internalAnchorEl, setInternalAnchorEl] = React.useState<HTMLElement | null>(null);
  const internalOpen = Boolean(internalAnchorEl);

  const [selectedItems, setSelectedItems] = useState<string[]>(memoizedInitialFilters);
  const [selectedSort, setSelectedSort] = useState<string>(() => {
    // Convert from asc/desc to ascending/descending for internal state
    if (memoizedInitialSort === "asc") return "ascending";
    if (memoizedInitialSort === "desc") return "descending";
    return "";
  });

  // Sync internal state with initial props when they change - use memoized values
  useEffect(() => {
    setSelectedItems(memoizedInitialFilters);
  }, [memoizedInitialFilters]);

  useEffect(() => {
    // Convert from asc/desc to ascending/descending for internal state
    const convertedSort = mapSortToAscdesc(memoizedInitialSort || "");
    setSelectedSort(convertedSort);
  }, [memoizedInitialSort]);

  useEffect(() => {
    if (!initialSort && initialFilters.length === 0 && !internalAnchorEl) {
      setSelectedItems([]);
      setSelectedSort("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalAnchorEl]);

  // Helper functions to convert between sort formats
  const mapSortToAscdesc = (sort: string): string => {
    if (sort === "asc") return "ascending";
    if (sort === "desc") return "descending";
    return "";
  };

  const mapAscdescToSort = (ascdesc: string): string => {
    if (ascdesc === "ascending") return "asc";
    if (ascdesc === "descending") return "desc";
    return "";
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setInternalAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setInternalAnchorEl(null);
  };

  const handleApply = () => {
    const convertedSort = mapAscdescToSort(selectedSort);
    callbackApplyFilters({ sort: convertedSort, filters: selectedItems });
    setInternalAnchorEl(null);
  };

  const handleCancel = () => {
    setInternalAnchorEl(null);
  };
  const theme = useTheme();
  const iconColor =
    initialSort != "" || initialFilters.length > 0 ? theme.palette.primary.main : "black";

  const handleReset = () => {
    setSelectedItems([]);
    setSelectedSort("");
    callbackApplyFilters({ sort: "", filters: [] });
    setInternalAnchorEl(null);
  };
  return (
    <div className="column_header_container">
      <button
        data-testid="menu_open"
        onClick={handleFilterClick}
        className="row_header_btn"
        style={{ minWidth: "200px" }}
      >
        <div className="flex space-between align-items-center">
          <div className="flex gap-10 align-items-center">
            <Typography fontWeight="bold">{columnName}</Typography>
            {initialFilters.length > 0 && (
              <Icon icon="mingcute:filter-line" width="15" height="15" color={iconColor} />
            )}
            {initialSort != "" && (
              <Icon icon="solar:arrow-down-outline" width="15" height="15" color={iconColor} />
            )}
          </div>

          <Icon icon="bxs:down-arrow" width="10" height="10" color={iconColor} />
        </div>
      </button>
      <div
        className="action_menu"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Menu
          id="basic-menu"
          anchorEl={internalAnchorEl}
          open={internalOpen}
          onClose={handleClose}
          slotProps={{
            paper: {
              "aria-labelledby": "basic-button",
            },
          }}
          sx={{
            "& .MuiPaper-root": {
              boxShadow: "1px 2px 2px rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <div className="flex flow-column gap-10 filter_container">
            <div className="flex flow-column width-100">
              <div className="flex space-between">
                {sortable && <h2>Sort</h2>}
                <button onClick={handleClose} className="close_icon">
                  <Icon icon="bitcoin-icons:cross-filled" height={20} color="black" />
                </button>
              </div>
              {sortable && (
                <Sort
                  selectedButton={selectedSort}
                  handleSortClick={(sort: string) => {
                    setSelectedSort(sort);
                  }}
                />
              )}
            </div>
            {filterable && (
              <>
                <h2>Filter</h2>
                <Filter
                  data={data}
                  initialSelected={selectedItems}
                  callbackSelected={(selected: string[]) => setSelectedItems(selected)}
                />
              </>
            )}
            <button
              className="reset_btn_filter"
              onClick={handleReset}
              disabled={selectedItems.length === 0 && selectedSort === ""}
            >
              <h4>Reset</h4>
            </button>
            <div className="filter_footer">
              <button onClick={handleCancel} className="btn filter_cancel">
                Cancel
              </button>
              <button onClick={handleApply} className="btn filter_apply">
                Apply
              </button>
            </div>
          </div>
        </Menu>
      </div>
    </div>
  );
};
